import { siteInfo, articles } from '@/mocks/site';

interface SchemaOrgProps {
  type: 'WebSite' | 'WebPage' | 'Article' | 'BreadcrumbList' | 'Organization' | 'FAQPage';
  data?: Record<string, unknown>;
}

export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  const getSchema = () => {
    const baseUrl = 'https://www.heiliaochigua.com';

    switch (type) {
      case 'Organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: siteInfo.name,
          url: baseUrl,
          description: siteInfo.description,
          email: siteInfo.email,
        };

      case 'WebSite':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteInfo.name,
          url: baseUrl,
          description: siteInfo.description,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${baseUrl}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        };

      case 'WebPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: data?.name || siteInfo.name,
          description: data?.description || siteInfo.description,
          url: data?.url || baseUrl,
          ...(data?.breadcrumb && { breadcrumb: data.breadcrumb }),
        };

      case 'Article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data?.headline || '',
          description: data?.description || '',
          image: data?.image || '',
          datePublished: data?.datePublished || '',
          dateModified: data?.dateModified || data?.datePublished || '',
          author: {
            '@type': 'Person',
            name: data?.author || siteInfo.name,
          },
          publisher: {
            '@type': 'Organization',
            name: siteInfo.name,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': data?.url || baseUrl,
          },
        };

      case 'BreadcrumbList':
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: (data?.items as Array<{ name: string; url: string }>)?.map(
            (item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.name,
              item: item.url,
            })
          ) || [],
        };

      case 'FAQPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: (data?.questions as Array<{ question: string; answer: string }>)?.map(
            (q) => ({
              '@type': 'Question',
              name: q.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: q.answer,
              },
            })
          ) || [],
        };

      default:
        return null;
    }
  };

  const schema = getSchema();
  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}